import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt } from 'react-icons/fa';
import { InferType, number, object, ObjectSchema, string } from 'yup';

import { AssetClass, RoomReadModel, RoomType, SetRoomPayload, StandardType } from '@client/api';
import {
  addressValueSchema,
  AreaAdornment,
  Button,
  FormField,
  Input,
  Select,
  spaceSourceExternalReferenceValueSchema,
  TableCell,
  TableRow,
  transformToOptionalNumber,
} from '@client/toolkit';

const ROOM_TYPE_OPTIONS: RoomType[] = [
  'Bedroom',
  'LivingRoom',
  'Kitchen',
  'Bathroom',
  'Toilet',
  'StorageRoom',
  'Office',
  'Other',
];

type FormModel = SetRoomPayload;

const schema: ObjectSchema<FormModel> = object({
  name: string().required(),
  description: string().nullable(),
  address: addressValueSchema.nullable().makeOptional(),
  areaMetric: number().min(0).transform(transformToOptionalNumber),
  source: spaceSourceExternalReferenceValueSchema.nullable().makeOptional(),
  standard: string<StandardType>().nullable(),
  type: string<RoomType>().required(),
  id: string().nullable(),
  assetClass: string<AssetClass>().nullable(),
});

type FormValues = InferType<typeof schema>;

export const residentialUnitRoomSchema = schema;
export type ResidentialUnitRoomFormData = FormValues;

interface ResidentialUnitRoomItemFormProps {
  fieldIndex: number;
  room?: RoomReadModel;
  isFetching?: boolean;
  onRemove?: (fieldIndex: number) => void;
}

export function ResidentialUnitRoomItemForm({ fieldIndex, isFetching, onRemove }: ResidentialUnitRoomItemFormProps) {
  const { t: tType } = useTranslation('type');

  return (
    <TableRow>
      <TableCell loading={isFetching}>
        <FormField
          name={`rooms.${fieldIndex}.name`}
          defaultValue={''}
          render={({ field }) => <Input size="small" {...field} />}
        />
      </TableCell>

      <TableCell loading={isFetching}>
        <FormField
          name={`rooms.${fieldIndex}.type`}
          defaultValue={''}
          render={({ field }) => (
            <Select size="small" {...field}>
              <option value="" />
              {ROOM_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {tType(`RoomType.${type}`)}
                </option>
              ))}
            </Select>
          )}
        />
      </TableCell>

      <TableCell loading={isFetching}>
        <FormField
          name={`rooms.${fieldIndex}.areaMetric`}
          defaultValue={''}
          render={({ field }) => (
            <AreaAdornment>
              <Input type="number" size="small" className="pr-8" {...field} />
            </AreaAdornment>
          )}
        />
      </TableCell>

      <TableCell loading={isFetching}>
        <FormField
          name={`rooms.${fieldIndex}.description`}
          defaultValue={''}
          render={({ field }) => <Input size="small" {...field} />}
        />
      </TableCell>
      <TableCell align="right">
        <Button
          className="ml-2"
          size="xs"
          color="secondary-light"
          disabled={typeof onRemove !== 'function' || isFetching}
          onClick={() => {
            if (typeof onRemove !== 'function') {
              return;
            }
            onRemove(fieldIndex);
          }}
        >
          <FaRegTrashAlt />
        </Button>
      </TableCell>
    </TableRow>
  );
}
